import React, { useCallback, useState } from 'react'
import { AxiosResponse } from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress } from '@material-ui/core'
import { Text, Title } from '@siftscience/focus-components/text'
import { MultiSelect } from '@siftscience/focus-components/select'
import { Checkmark, Close, Delete } from '@siftscience/focus-components/icons'
import { IconButton } from '@siftscience/focus-components/button'

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    boxSizing: 'border-box'
  },
  emptyStateWrapper: {
    height: '100%',
    border: '2px dashed rgba(224, 224, 224, 1)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: 'rgba(234, 241, 254, 1)'
    }
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    height: '100%'
  },
  sidebar: {
    borderRight: '1px solid rgba(224, 224, 224, 1)',
    overflowY: 'auto',
    backgroundColor: 'rgba(255, 255, 255, 1)'
  },
  itemWrapper: {
    padding: '8px 10px',
    borderRadius: '6px 0 0 6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '6px'
  },
  active: {
    backgroundColor: 'rgba(234, 241, 254, 1)'
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    background: 'rgba(247, 247, 247, 1)'
  },
  dropOverlay: {
    position: 'absolute',
    inset: 0,
    border: '2px dashed #3f51b5',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(63, 81, 181, 0.04)',
    pointerEvents: 'none'
  },
  hiddenInput: {
    display: 'none'
  },
  previewImg: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    borderRadius: 4,
    boxShadow: '0 0 10px rgba(0,0,0,0.15)',
    objectFit: 'contain'
  },
  multiselect: {
    width: '100%',
    '& button': {
      width: '100%'
    }
  }
}))

type UploadStatus = 'uploading' | 'success' | 'error'

interface UploadItem {
  id: string
  file: File
  name: string
  status: UploadStatus
  previewUrl: string
  error?: string
  evidence?: { id: number | string }
}

interface UploadImageProps {
  onUploadImage: (file: File) => Promise<AxiosResponse>
  allowMultiple?: boolean
  allMids: { value: string; label: string }[]
  onDelete: (id: string) => Promise<void>
  onUpdateEvidence: (
    evidenceId: number | string,
    formData: FormData
  ) => Promise<AxiosResponse>
  selectedMids?: string[]
}

const UploadImage: React.FC<UploadImageProps> = ({
  onUploadImage,
  allowMultiple = false,
  onDelete,
  onUpdateEvidence,
  allMids,
  selectedMids
}) => {
  const classes = useStyles()
  const [items, setItems] = useState<UploadItem[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [imageMids, setImageMids] = useState<Record<string, string[]>>({})
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return

      const fileList = allowMultiple ? Array.from(files) : [files[0]]

      fileList.forEach(file => {
        const id = `${file.name}-${file.size}-${
          file.lastModified
        }-${Math.random()}`
        const reader = new FileReader()

        reader.onload = () => {
          const previewUrl = reader.result as string

          setItems(prev => {
            const newItem: UploadItem = {
              id,
              file,
              name: file.name,
              status: 'uploading',
              previewUrl
            }

            if (allowMultiple) {
              return [...prev, newItem]
            }

            return [newItem]
          })

          setImageMids(prev => ({
            ...prev,
            [id]: !selectedMids.length
              ? allMids.map(item => item.value)
              : selectedMids
          }))

          setSelectedId(prevSelectedId =>
            allowMultiple ? prevSelectedId ?? id : id
          )

          onUploadImage(file)
            .then(response => {
              setItems(prev =>
                prev.map(item =>
                  item.id === id
                    ? {
                        ...item,
                        evidence: response.data.evidence,
                        status: 'success'
                      }
                    : item
                )
              )
            })
            .catch(err => {
              setItems(prev =>
                prev.map(item =>
                  item.id === id
                    ? {
                        ...item,
                        status: 'error',
                        error: err?.message || 'Error'
                      }
                    : item
                )
              )
            })
        }

        reader.readAsDataURL(file)
      })
    },
    [onUploadImage, allowMultiple]
  )

  const handleDrop: React.DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging) setIsDragging(true)
  }

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const openFileDialog = () => {
    // eslint-disable-next-line no-unused-expressions
    fileInputRef.current?.click()
  }

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    handleFiles(e.target.files)
    e.target.value = ''
  }

  const onDeleteEvidence = (id: string) => {
    const evidenceId = items.find(i => i.id === id)?.evidence?.id
    if (evidenceId) {
      onDelete(`${evidenceId}`)
    }
    setItems(prev => prev.filter(item => item.id !== id))
  }

  const onMidsChange = (updatedMids: { value: string }[], id: string) => {
    const formData = new FormData()

    if (updatedMids.length === 0) {
      formData.append('evidence[mid_ids][]', '')
    }

    updatedMids.forEach(item =>
      formData.append('evidence[mid_ids][]', item.value)
    )

    const evidenceId = items.find(i => i.id === id)?.evidence?.id

    onUpdateEvidence(evidenceId, formData)

    setImageMids(prev => ({
      ...prev,
      [id]: updatedMids.map(item => item.value)
    }))
  }

  const selectedItem = items.find(i => i.id === selectedId) || items[0]

  if (items.length === 0) {
    return (
      <div
        className={classes.container}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          className={classes.hiddenInput}
          type="file"
          accept="image/*"
          multiple={allowMultiple}
          onChange={onFileChange}
        />
        <div className={classes.emptyStateWrapper}>
          <Title size="large" color="primary">
            Drop image{allowMultiple ? 's' : ''} here
          </Title>
          <Text color="primary" size="medium">
            JPG, PNG, WebP, GIF or SVG, max 5 MB
          </Text>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <input
        ref={fileInputRef}
        className={classes.hiddenInput}
        type="file"
        accept="image/*"
        multiple={allowMultiple}
        onChange={onFileChange}
      />

      <div className={classes.grid}>
        <div className={classes.sidebar}>
          {items.map(item => (
            <div
              className={[
                classes.itemWrapper,
                item.id === selectedId ? classes.active : ''
              ].join(' ')}
              key={item.id}
              onClick={() => setSelectedId(item.id)}
            >
              <div className={classes.item}>
                {item.status === 'uploading' && <CircularProgress size={18} />}
                {item.status === 'success' && (
                  <Checkmark
                    color="rgba(5, 161, 49, 1)"
                    width={16}
                    height={16}
                  />
                )}
                {item.status === 'error' && (
                  <Close width={16} height={16} color="rgba(235, 59, 46, 1)" />
                )}
                <div>
                  <Title size="small">{item.name}</Title>
                  <MultiSelect
                    className={classes.multiselect}
                    selectedItems={allMids.filter(midItem =>
                      imageMids[item.id]?.includes(midItem.value)
                    )}
                    variant={'secondary'}
                    items={allMids}
                    selectedItemsRenderer={() =>
                      allMids.length === imageMids[item.id].length ? (
                        <Text size="xsmall">All MIDs</Text>
                      ) : (
                        <Text size="xsmall">
                          {imageMids[item.id].length} MIDs selected
                        </Text>
                      )
                    }
                    onChange={(updatedMids: { value: string }[]) =>
                      onMidsChange(updatedMids, item.id)
                    }
                    stretch={false}
                    placeholder="Select MIDs"
                  />
                </div>
              </div>
              <IconButton onClick={() => onDeleteEvidence(item.id)}>
                <Delete width={16} height={16} />
              </IconButton>
            </div>
          ))}
        </div>

        <div
          className={classes.content}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={openFileDialog}
        >
          {isDragging && (
            <div className={classes.dropOverlay}>
              <Title color="primary" size="large">
                Drop image{allowMultiple ? 's' : ''} to upload
              </Title>
            </div>
          )}

          {!selectedItem || selectedItem.status === 'uploading' ? (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress />
              <Title color="primary" size="large">
                Image is loading...
              </Title>
            </div>
          ) : selectedItem.status === 'error' ? (
            <Title color="primary" size="large">
              Failed to upload image: {selectedItem.error || 'Error'}
            </Title>
          ) : (
            <img
              src={selectedItem.previewUrl}
              alt={selectedItem.name}
              className={classes.previewImg}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default UploadImage
